/**
 * お問合せ自動化機能の API hooks。
 * 仕様書: docs/01_spec/spec_inquiry_automation.md C-8
 *
 * Callable Functions:
 *   - inquiryQueueList: 事務承認キュー取得
 *   - inquiryQueueResolve: 事務承認キュー処理
 *
 * Firestore 直接 read:
 *   - qa_categories（カテゴリ一覧 + リアルタイム購読）
 *   - qa_history（QA 個別 read / update）
 *   - inquiry_queue（onSnapshot で新着検知）
 *   - settings/inquiry_automation
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitFn,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';

import {
  DEFAULT_DATA_RETENTION,
  INQUIRY_AUTOMATION_SETTINGS_DOC_ID,
  type DataRetentionSettings,
  type InquiryAutomationSettings,
  type InquiryQueueItem,
  type InquiryQueueResolution,
  type QACategory,
  type QAHistory,
} from '@goenhub/shared';

import { db, functions } from '@/lib/firebase';
import { safeUpdateDoc } from '@/lib/safeWrite';

// ─────────────────────────────────────────────
// 事務承認キュー
// ─────────────────────────────────────────────

export interface InquiryQueueRecord extends InquiryQueueItem {
  id: string;
}

const inquiryQueueListFn = httpsCallable<
  { status?: InquiryQueueItem['status'] | 'all'; limit?: number },
  { items: InquiryQueueRecord[]; total: number }
>(functions, 'inquiryQueueList');

export function useInquiryQueue(status: InquiryQueueItem['status'] | 'all' = 'pending') {
  return useQuery({
    queryKey: ['inquiry_queue', status],
    queryFn: async () => {
      const res = await inquiryQueueListFn({ status, limit: 100 });
      return res.data.items;
    },
    refetchInterval: 30_000, // 30秒ごとに自動更新
  });
}

/**
 * リアルタイム新着検知（onSnapshot）。
 * useInquiryQueue とは別系統で、新着件数のバッジ表示用。
 */
export function useInquiryQueuePendingCount(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const q = query(collection(db, 'inquiry_queue'), where('status', '==', 'pending'));
    const unsub = onSnapshot(q, (snap) => {
      setCount(snap.size);
    });
    return unsub;
  }, []);
  return count;
}

const inquiryQueueResolveFn = httpsCallable<
  { queueId: string; resolution: InquiryQueueResolution; finalAnswer?: string },
  { ok: true; qaHistoryId: string | null }
>(functions, 'inquiryQueueResolve');

export function useResolveInquiryQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      queueId: string;
      resolution: InquiryQueueResolution;
      finalAnswer?: string;
    }) => {
      const res = await inquiryQueueResolveFn(args);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inquiry_queue'] });
      qc.invalidateQueries({ queryKey: ['qa_history'] });
    },
  });
}

// ─────────────────────────────────────────────
// QA カテゴリ
// ─────────────────────────────────────────────

export interface QACategoryRecord extends QACategory {
  id: string;
}

export function useQACategories() {
  return useQuery({
    queryKey: ['qa_categories'],
    queryFn: async (): Promise<QACategoryRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'qa_categories'), orderBy('createdAt', 'desc')),
      );
      const items: QACategoryRecord[] = [];
      snap.forEach((d) => {
        const data = d.data() as QACategory;
        if (!data.archivedAt) {
          items.push({ ...data, id: d.id });
        }
      });
      return items;
    },
  });
}

export function useUpsertQACategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id?: string;
      name: string;
      slug: string;
      description: string;
      aiAutoEnabled: boolean;
      confidenceThreshold: number;
      escalationKeywords: string[];
    }) => {
      const now = new Date();
      const ref = args.id
        ? doc(db, 'qa_categories', args.id)
        : doc(collection(db, 'qa_categories'));
      const payload: Partial<QACategory> = {
        name: args.name,
        slug: args.slug,
        description: args.description,
        aiAutoEnabled: args.aiAutoEnabled,
        confidenceThreshold: args.confidenceThreshold,
        escalationKeywords: args.escalationKeywords,
        updatedAt: now.toISOString(),
        archivedAt: null,
      };
      if (!args.id) {
        Object.assign(payload, {
          parentCategoryId: null,
          qaCount: 0,
          averageRating: null,
          createdBy: 'admin',
          createdAt: now.toISOString(),
        });
        await setDoc(ref, payload);
      } else {
        await safeUpdateDoc(ref, payload);
      }
      return ref.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qa_categories'] });
    },
  });
}

export function useArchiveQACategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (categoryId: string) => {
      await safeUpdateDoc(doc(db, 'qa_categories', categoryId), {
        archivedAt: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qa_categories'] });
    },
  });
}

// ─────────────────────────────────────────────
// qa_history （カテゴリ別 QA 一覧）
// ─────────────────────────────────────────────

export interface QAHistoryRecord extends Omit<QAHistory, 'embedding'> {
  id: string;
}

export function useQAByCategoryAndChannel(args: {
  categoryId: string | null;
  channels?: Array<'line' | 'public' | 'both'>;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['qa_history_by_category', args.categoryId, args.channels],
    queryFn: async (): Promise<QAHistoryRecord[]> => {
      const conditions = [];
      if (args.categoryId === null) {
        // 未分類プール（categoryId=null）
        conditions.push(where('categoryId', '==', null));
      } else {
        conditions.push(where('categoryId', '==', args.categoryId));
      }
      const channels = args.channels ?? ['public', 'both'];
      if (channels.length > 0 && channels.length < 3) {
        conditions.push(where('channel', 'in', channels));
      }
      const q = query(
        collection(db, 'qa_history'),
        ...conditions,
        orderBy('createdAt', 'desc'),
        limitFn(args.limit ?? 50),
      );
      const snap = await getDocs(q);
      const items: QAHistoryRecord[] = [];
      snap.forEach((d) => {
        const data = d.data() as QAHistory;
        const { embedding, ...rest } = data;
        items.push({ ...rest, id: d.id });
      });
      return items;
    },
  });
}

export function useUpdateQAHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      question?: string;
      answer?: string;
      adminCorrectedAnswer?: string | null;
      adminApproved?: boolean;
      categoryId?: string | null;
    }) => {
      const { id, ...patch } = args;
      await safeUpdateDoc(doc(db, 'qa_history', id), {
        ...patch,
        adminReviewed: true,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qa_history_by_category'] });
      qc.invalidateQueries({ queryKey: ['qa_history'] });
    },
  });
}

// ─────────────────────────────────────────────
// 手動 QA 追加 / 編集 (callable 経由・embedding 再生成対応)
// ─────────────────────────────────────────────

const inquiryAddManualQAFn = httpsCallable<
  { question: string; answer: string; categoryId?: string | null; adminApproved?: boolean },
  { ok: true; qaHistoryId: string }
>(functions, 'inquiryAddManualQA');

export function useAddManualQA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      question: string;
      answer: string;
      categoryId?: string | null;
      adminApproved?: boolean;
    }) => {
      const res = await inquiryAddManualQAFn(args);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qa_history_by_category'] });
      qc.invalidateQueries({ queryKey: ['qa_history'] });
      qc.invalidateQueries({ queryKey: ['qa_categories'] });
    },
  });
}

const inquiryUpdateQAItemFn = httpsCallable<
  {
    id: string;
    question?: string;
    answer?: string;
    adminCorrectedAnswer?: string | null;
    categoryId?: string | null;
    adminApproved?: boolean;
  },
  { ok: true; embeddingRegenerated: boolean }
>(functions, 'inquiryUpdateQAItem');

/**
 * 既存 QA を編集する callable 経由 hook。
 * question 変更時は backend で embedding を再生成する。
 */
export function useUpdateQAItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      question?: string;
      answer?: string;
      adminCorrectedAnswer?: string | null;
      categoryId?: string | null;
      adminApproved?: boolean;
    }) => {
      const res = await inquiryUpdateQAItemFn(args);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qa_history_by_category'] });
      qc.invalidateQueries({ queryKey: ['qa_history'] });
      qc.invalidateQueries({ queryKey: ['qa_categories'] });
    },
  });
}

const inquiryDeleteQAItemFn = httpsCallable<{ id: string }, { ok: true }>(
  functions,
  'inquiryDeleteQAItem',
);

/**
 * QA をハード削除する callable 経由 hook。
 * backend で qa_categories.qaCount の -1 補正も行う。
 */
export function useDeleteQAItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await inquiryDeleteQAItemFn({ id });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qa_history_by_category'] });
      qc.invalidateQueries({ queryKey: ['qa_history'] });
      qc.invalidateQueries({ queryKey: ['qa_categories'] });
    },
  });
}

// ─────────────────────────────────────────────
// 設定
// ─────────────────────────────────────────────

const DEFAULT_SETTINGS: InquiryAutomationSettings = {
  aiAutoEnabled: true,
  defaultConfidenceThreshold: 0.85,
  generateDraftWhenOff: true,
  escalationNotifyChannels: ['browser'],
  escalationNotifyRecipients: [],
  reviewCardEnabled: true,
  reviewCardDelaySeconds: 2,
  piiMaskingEnabled: true,
  updatedAt: new Date().toISOString(),
  updatedBy: 'system',
};

export function useInquirySettings() {
  return useQuery({
    queryKey: ['settings_inquiry_automation'],
    queryFn: async (): Promise<InquiryAutomationSettings> => {
      // settings collection 全件 getDocs だと collection-level query が必要で
      // doc 単位の firestore.rules では権限エラーになる場合があるため、
      // doc 直接 read に変更。
      const docRef = doc(db, 'settings', INQUIRY_AUTOMATION_SETTINGS_DOC_ID);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return DEFAULT_SETTINGS;
      return { ...DEFAULT_SETTINGS, ...(snap.data() as Partial<InquiryAutomationSettings>) };
    },
  });
}

export function useUpdateInquirySettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<InquiryAutomationSettings>) => {
      const ref = doc(db, 'settings', INQUIRY_AUTOMATION_SETTINGS_DOC_ID);
      await setDoc(
        ref,
        {
          ...patch,
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings_inquiry_automation'] });
    },
  });
}

// ─────────────────────────────────────────────
// データ保持設定
// ─────────────────────────────────────────────

export function useDataRetention(): DataRetentionSettings {
  const settingsQuery = useInquirySettings();
  return settingsQuery.data?.dataRetention ?? DEFAULT_DATA_RETENTION;
}

export function useUpdateDataRetention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<DataRetentionSettings>) => {
      const ref = doc(db, 'settings', INQUIRY_AUTOMATION_SETTINGS_DOC_ID);
      // 既存設定を merge してから dataRetention を更新
      await setDoc(
        ref,
        {
          dataRetention: { ...DEFAULT_DATA_RETENTION, ...patch },
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings_inquiry_automation'] });
    },
  });
}
